<script lang="ts">
	import MarketingSeoHead from '$lib/components/marketing/MarketingSeoHead.svelte';
	import * as Breadcrumb from '$lib/components/ui/breadcrumb/index.js';
	import { Separator } from '$lib/components/ui/separator/index.js';
	import * as Sidebar from '$lib/components/ui/sidebar/index.js';
	import { getMarketplaceSeoContent, type MarketplaceSeoKey } from '$lib/content/marketplace-seo';
	import { buildBarcosPath, type BarcosRoute } from '$lib/geo';
	import {
		absoluteUrl,
		faqStructuredData,
		marketingSeoMeta,
		type MarketingSeoMeta
	} from '$lib/utils/marketing-seo';
	import { buildMarketplaceSeoTitle } from '$lib/utils/seo-title';
	import ListingsMarket from '@/components/markets/listings-market.svelte';
	import MarketplaceSeoContent from '@/components/markets/seo/MarketplaceSeoContent.svelte';
	import SidebarMarketplace from '@/components/Sidebars/sidebar-marketplace.svelte';
	import type { FeaturedReview } from '@/routes/barcos/[...segments]/+page';
	import { getLocale } from '@/paraglide/runtime';
	import { page } from '$app/state';
	import type { ListingsResponse } from '@/pocketbase/pocketbase-types';

	let {
		route = null,
		cityName = null,
		portId = null,
		portName = null,
		initialListings = [],
		initialTotalItems = 0,
		initialTotalPages = 0,
		marketplaceSeoKey = null,
		featuredReviews = []
	}: {
		route?: BarcosRoute | null;
		cityName?: string | null;
		portId?: string | null;
		portName?: string | null;
		initialListings?: ListingsResponse[];
		initialTotalItems?: number;
		initialTotalPages?: number;
		marketplaceSeoKey?: MarketplaceSeoKey | null;
		featuredReviews?: FeaturedReview[];
	} = $props();

	const locationLabel = $derived.by(() => {
		if (!route) return null;
		if (portName) return portName;
		if (cityName) return cityName;
		if (route.comunidad && !route.comunidad.uniprovincial && route.provincia) {
			return route.provincia;
		}
		return route.comunidad?.name ?? null;
	});

	const canonicalPath = $derived(
		route
			? buildBarcosPath({
					tipo: route.tipo,
					comunidad: route.comunidad,
					provincia: route.provincia,
					ciudad: route.ciudadSlug,
					puerto: route.puertoSlug
				})
			: '/barcos'
	);

	const locale = $derived(getLocale());
	const seoPageContent = $derived.by(() => {
		void locale;
		return marketplaceSeoKey ? getMarketplaceSeoContent(marketplaceSeoKey) : null;
	});

	const seoMeta = $derived.by((): MarketingSeoMeta | null => {
		void locale;
		if (seoPageContent) {
			return seoPageContent.meta;
		}

		const base = marketingSeoMeta('/barcos');
		if (!route || (!route.tipo && !locationLabel)) return base;

		const title = buildMarketplaceSeoTitle({
			tipo: route.tipo,
			locationLabel
		});
		const what =
			route.tipo === 'experiencia'
				? 'Experiencias náuticas'
				: route.tipo === 'alquiler'
					? 'Alquiler de barcos'
					: 'Alquiler de barcos y experiencias náuticas';
		const where = locationLabel ? ` en ${locationLabel}` : '';
		return {
			title,
			description: `${what}${where}. Compara precios, reserva con armadores locales y zarpa con Zarpar.`
		};
	});

	const seoStructuredData = $derived.by(() => {
		if (!seoPageContent) return null;
		return faqStructuredData({
			title: seoPageContent.meta.title,
			description: seoPageContent.meta.description,
			pageUrl: absoluteUrl(page.url.pathname),
			items: seoPageContent.faqs
		});
	});
</script>

{#if seoMeta}
	<MarketingSeoHead
		meta={seoMeta}
		canonicalPath={canonicalPath}
		structuredData={seoStructuredData}
	/>
{/if}

<Sidebar.Provider>
	<SidebarMarketplace />
	<Sidebar.Inset>
		<header
			class="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12 md:h-0"
		>
			<div class="flex items-center gap-2 px-4">
				<Sidebar.Trigger class="-ml-1 md:hidden" />
				<Separator orientation="vertical" class="mr-2 h-4 md:hidden" />
				<Breadcrumb.Root class="md:hidden">
					<Breadcrumb.List>
						<span class="text-foreground font-normal md:hidden">Filtros</span>
					</Breadcrumb.List>
				</Breadcrumb.Root>
			</div>
		</header>
		{#key canonicalPath}
			<ListingsMarket
				route={route}
				cityName={cityName}
				portId={portId}
				portName={portName}
				initialListings={initialListings}
				initialTotalItems={initialTotalItems}
				initialTotalPages={initialTotalPages}
			/>
		{/key}

		{#if marketplaceSeoKey}
			<MarketplaceSeoContent seoKey={marketplaceSeoKey} featuredReviews={featuredReviews} />
		{/if}
	</Sidebar.Inset>
</Sidebar.Provider>
